// src/pages/EditHireAgreement.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Divider,
  Fab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  DialogActions,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import Autocomplete from '@mui/material/Autocomplete';
import { useNavigate, useParams } from 'react-router-dom';

import useDrivers from '../hooks/useDrivers';
import useVehicles from '../hooks/useVehicles';
import {
  fetchHireAgreement,
  updateHireAgreement,
  deleteHireAgreement,
} from '../api/hireAgreements';

import {
  getDocuments,
  uploadDocument,
  deleteDocument,
  downloadDocument,
} from '../api/documents';

// ---------- Small helpers ----------

// Read-only display that matches the style of your EditVehicle page
function ReadOnlyField({ label, value }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography
        variant="caption"
        sx={{ textTransform: 'uppercase', color: 'text.secondary' }}
      >
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

// Convert backend Date/ISO/whatever to YYYY-MM-DD for <input type="date">
function toDateInputValue(value) {
  if (!value) return '';
  let d;
  if (typeof value === 'string') {
    d = new Date(value);
  } else if (value && value.$date) {
    d = new Date(value.$date);
  } else if (value instanceof Date) {
    d = value;
  } else {
    d = new Date(value);
  }
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

// For view-mode formatted dates (dd/mm/yyyy)
function formatDateDisplay(value) {
  if (!value) return '';
  const d = new Date(value && value.$date ? value.$date : value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB');
}

// For view-mode date+time
function formatDateTimeDisplay(value) {
  if (!value) return '';
  const d = new Date(value && value.$date ? value.$date : value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB');
}

export default function EditHireAgreement() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { drivers } = useDrivers();
  const { vehicles } = useVehicles();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [agreement, setAgreement] = useState(null);
  const [form, setForm] = useState(null);

  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Hire agreement delete state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // File delete state (requires typing "confirm")
  const [fileDeleteOpen, setFileDeleteOpen] = useState(false);
  const [fileDeleteInput, setFileDeleteInput] = useState('');
  const [fileDeleteLoading, setFileDeleteLoading] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFileName, setUploadFileName] = useState("")
  const [uploadType, setUploadType] = useState("");
  const [uploadExpiry, setUploadExpiry] = useState("");
  const [uploadErrors, setUploadErrors] = useState({});

  // New: validation + generic error state
  const [errors, setErrors] = useState({});
  const [genericError, setGenericError] = useState('');

  const updateField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const clearFieldError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const loadAgreement = async () => {
    setLoading(true);
    setGenericError('');
    try {
      const data = await fetchHireAgreement(id);
      setAgreement(data);

      setForm({
        driver_id: data.driver_id?._id || data.driver_id || '',
        vehicle_id: data.vehicle_id?._id || data.vehicle_id || '',

        start_date: toDateInputValue(data.start_date),
        end_date: toDateInputValue(data.end_date),
        handover_date: toDateInputValue(data.handover_date),

        currency: data.currency || 'GBP',
        deposit_amount_pence: data.deposit_amount_pence || 0,
        weekly_amount_pence: data.weekly_amount_pence || 0,

        insurance_included: !!data.insurance_included,
        insurance_price_pence: data.insurance_price_pence || 0,

        renter_licence_number: data.renter_licence_number || '',
        status: data.status || 'draft',
        end_reason: data.end_reason || '',
      });
    } catch (err) {
      console.error('Failed to load hire agreement:', err);
      setGenericError(
        'Failed to load hire agreement. Please refresh or try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    setDocumentsLoading(true);
    try {
      // FIX: use entity_type + entity_id
      const docs = await getDocuments('hire_agreement', id);
      setDocuments(docs || []);
    } catch (err) {
      console.error('Failed to load documents', err);
      // optional: we can surface this in genericError if you want
    } finally {
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    loadAgreement();
    loadDocuments();
  }, [id]);

  useEffect(() => {
    setUploadFileName(uploadFile ? uploadFile.name.split('.')[0] : "")
  }, [uploadFile])

  const selectedDriver = useMemo(
    () =>
      drivers.find((d) => String(d._id) === String(form?.driver_id)) || null,
    [drivers, form]
  );

  const selectedVehicle = useMemo(
    () =>
      vehicles.find((v) => String(v._id) === String(form?.vehicle_id)) || null,
    [vehicles, form]
  );

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setErrors({});
    setGenericError('');
    try {
      const payload = {
        driver_id: form.driver_id || null,
        vehicle_id: form.vehicle_id || null,

        start_date: form.start_date || null,
        end_date: form.end_date || null,
        handover_date: form.handover_date || null,

        currency: form.currency,
        deposit_amount_pence: Number(form.deposit_amount_pence || 0),
        weekly_amount_pence: Number(form.weekly_amount_pence || 0),

        insurance_included: !!form.insurance_included,
        insurance_price_pence: Number(form.insurance_price_pence || 0),

        renter_licence_number: form.renter_licence_number,
        status: form.status,
        end_reason:
          form.status === 'ended' || form.status === 'cancelled'
            ? form.end_reason
            : '',
      };

      await updateHireAgreement(id, payload);
      await loadAgreement();
      setEditMode(false);
    } catch (err) {
      console.error('Failed to update hire agreement:', err);
      if (err.status === 400 && err.validationErrors) {
        // express-validator style: { field: "message", ... }
        setErrors(err.validationErrors || {});
      } else {
        setGenericError(
          'A server error occurred while saving. Please try again.'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEdit = () => {
    if (editMode) {
      handleSave();
    } else {
      setEditMode(true);
    }
  };

const handleUploadClick = () => {
  if (fileInputRef.current) fileInputRef.current.click();
};

const handleFileChange = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;
  // BEFORE: direct upload
  // NOW: open modal
  setUploadFile(file);
  setUploadModalOpen(true);
};

  const handleDownload = (docId) => {
    downloadDocument(docId);
  };

  // -----------------------------
  // Hire agreement deletion logic
  // -----------------------------

  const handleOpenDeleteAgreement = () => {
    setDeleteInput('');
    setDeleteOpen(true);
  };

  const handleCloseDeleteAgreement = () => {
    if (deleteLoading) return;
    setDeleteOpen(false);
    setDeleteInput('');
  };

  const handleConfirmDeleteAgreement = async () => {
    setDeleteLoading(true);
    setGenericError('');
    try {
      await deleteHireAgreement(id);
      navigate('/hire-agreements');
    } catch (err) {
      console.error('Failed to delete hire agreement:', err);
      setGenericError(
        'Failed to delete hire agreement. Please try again or contact support.'
      );
    } finally {
      setDeleteLoading(false);
      setDeleteOpen(false);
    }
  };

  // Require user to type "confirm" to delete
  const deleteAgreementDisabled =
    !deleteInput.trim() ||
    deleteInput.trim().toLowerCase() !== 'confirm';

  // -----------------------------
  // File deletion logic (type "confirm")
  // -----------------------------

  const handleRequestDeleteDoc = (doc) => {
    setFileToDelete(doc);
    setFileDeleteInput('');
    setFileDeleteOpen(true);
  };

  const handleCloseFileDelete = () => {
    if (fileDeleteLoading) return;
    setFileDeleteOpen(false);
    setFileToDelete(null);
    setFileDeleteInput('');
  };

  const handleConfirmFileDelete = async () => {
    if (!fileToDelete) return;
    setFileDeleteLoading(true);
    try {
      await deleteDocument(fileToDelete._id);
      await loadDocuments();
    } catch (err) {
      console.error('Failed to delete document:', err);
      setGenericError('Failed to delete document. Please try again.');
    } finally {
      setFileDeleteLoading(false);
      setFileDeleteOpen(false);
      setFileToDelete(null);
      setFileDeleteInput('');
    }
  };

  const fileDeleteDisabled =
    !fileToDelete ||
    !fileDeleteInput.trim() ||
    fileDeleteInput.trim().toLowerCase() !== 'confirm';

  if (loading || !form) {
    return (
      <Box sx={{ mt: 5, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', pb: 8 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Hire Agreement
      </Typography>

      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        View and edit this hire agreement. Editing is disabled until you click the
        floating edit button, just like the driver and vehicle pages.
      </Typography>

      {genericError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setGenericError('')}
        >
          {genericError}
        </Alert>
      )}

      {/* Main details card */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        {/* Parties */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Hire Agreement Parties
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12, sm: 6}}>
            {editMode ? (
              <Autocomplete
                options={drivers}
                getOptionLabel={(d) =>
                  `${d.firstname || ''} ${d.lastname || ''}`.trim()
                }
                value={selectedDriver}
                onChange={(e, val) => {
                  updateField('driver_id', val ? val._id : '');
                  clearFieldError('driver_id');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Driver"
                    fullWidth
                    error={!!errors.driver_id}
                    helperText={errors.driver_id}
                  />
                )}
              />
            ) : (
              <ReadOnlyField
                label="Driver"
                value={
                  selectedDriver
                    ? `${selectedDriver.firstname || ''} ${
                        selectedDriver.lastname || ''
                      }`.trim()
                    : 'Unassigned'
                }
              />
            )}
          </Grid>
          <Grid item size={{xs: 12, sm: 6}}>
            {editMode ? (
              <Autocomplete
                options={vehicles}
                getOptionLabel={(v) =>
                  `${v.vrm} – ${v.make_model || v.make || ''}`
                }
                value={selectedVehicle}
                onChange={(e, val) => {
                  updateField('vehicle_id', val ? val._id : '');
                  clearFieldError('vehicle_id');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vehicle"
                    fullWidth
                    error={!!errors.vehicle_id}
                    helperText={errors.vehicle_id}
                  />
                )}
              />
            ) : (
              <ReadOnlyField
                label="Vehicle"
                value={
                  selectedVehicle
                    ? `${selectedVehicle.vrm} – ${
                        selectedVehicle.make_model ||
                        selectedVehicle.make ||
                        ''
                      }`
                    : 'Unassigned'
                }
              />
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Hire period */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Hire Period
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12, sm: 4}}>
            {editMode ? (
              <TextField
                label="Handover Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.handover_date}
                onChange={(e) => {
                  updateField('handover_date', e.target.value);
                  clearFieldError('handover_date');
                }}
                error={!!errors.handover_date}
                helperText={errors.handover_date}
              />
            ) : (
              <ReadOnlyField
                label="Handover Date"
                value={formatDateDisplay(agreement.handover_date)}
              />
            )}
          </Grid>
          <Grid item size={{xs: 12, sm: 4}}>
            {editMode ? (
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.start_date}
                onChange={(e) => {
                  updateField('start_date', e.target.value);
                  clearFieldError('start_date');
                }}
                error={!!errors.start_date}
                helperText={errors.start_date}
              />
            ) : (
              <ReadOnlyField
                label="Start Date"
                value={formatDateDisplay(agreement.start_date)}
              />
            )}
          </Grid>
          <Grid item size={{xs: 12, sm: 4}}>
            {editMode ? (
              <TextField
                label="End Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.end_date}
                onChange={(e) => {
                  updateField('end_date', e.target.value);
                  clearFieldError('end_date');
                }}
                error={!!errors.end_date}
                helperText={errors.end_date}
              />
            ) : (
              <ReadOnlyField
                label="End Date"
                value={formatDateDisplay(agreement.end_date)}
              />
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Financial details */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Financial Details
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12, sm: 4}}>
            {editMode ? (
              <TextField
                label="Currency"
                select
                fullWidth
                value={form.currency}
                onChange={(e) => {
                  updateField('currency', e.target.value);
                  clearFieldError('currency');
                }}
                error={!!errors.currency}
                helperText={errors.currency}
              >
                <MenuItem value="GBP">GBP</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
              </TextField>
            ) : (
              <ReadOnlyField label="Currency" value={form.currency} />
            )}
          </Grid>
          <Grid item size={{xs: 12, sm: 4}}>
            {editMode ? (
              <TextField
                label="Deposit (pence)"
                type="number"
                fullWidth
                value={form.deposit_amount_pence}
                onChange={(e) => {
                  updateField('deposit_amount_pence', e.target.value);
                  clearFieldError('deposit_amount_pence');
                }}
                error={!!errors.deposit_amount_pence}
                helperText={errors.deposit_amount_pence}
              />
            ) : (
              <ReadOnlyField
                label="Deposit (pence)"
                value={String(form.deposit_amount_pence)}
              />
            )}
          </Grid>
          <Grid item size={{xs: 12, sm: 4}}>
            {editMode ? (
              <TextField
                label="Weekly Amount (pence)"
                type="number"
                fullWidth
                value={form.weekly_amount_pence}
                onChange={(e) => {
                  updateField('weekly_amount_pence', e.target.value);
                  clearFieldError('weekly_amount_pence');
                }}
                error={!!errors.weekly_amount_pence}
                helperText={errors.weekly_amount_pence}
              />
            ) : (
              <ReadOnlyField
                label="Weekly Amount (pence)"
                value={String(form.weekly_amount_pence)}
              />
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Insurance */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Insurance
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12}}>
            {editMode ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.insurance_included}
                    onChange={(e) => {
                      updateField('insurance_included', e.target.checked);
                      clearFieldError('insurance_included');
                    }}
                  />
                }
                label="Insurance Included"
              />
            ) : (
              <ReadOnlyField
                label="Insurance Included"
                value={form.insurance_included ? 'Yes' : 'No'}
              />
            )}
          </Grid>

          {form.insurance_included && (
            <Grid item size={{xs: 12, sm: 6}}>
              {editMode ? (
                <TextField
                  label="Insurance Price (pence)"
                  type="number"
                  fullWidth
                  value={form.insurance_price_pence}
                  onChange={(e) => {
                    updateField('insurance_price_pence', e.target.value);
                    clearFieldError('insurance_price_pence');
                  }}
                  error={!!errors.insurance_price_pence}
                  helperText={errors.insurance_price_pence}
                />
              ) : (
                <ReadOnlyField
                  label="Insurance Price (pence)"
                  value={String(form.insurance_price_pence)}
                />
              )}
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Renter details */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Renter Details
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12, sm: 6}}>
            {editMode ? (
              <TextField
                label="Renter Licence Number"
                fullWidth
                value={form.renter_licence_number}
                onChange={(e) => {
                  updateField('renter_licence_number', e.target.value);
                  clearFieldError('renter_licence_number');
                }}
                error={!!errors.renter_licence_number}
                helperText={errors.renter_licence_number}
              />
            ) : (
              <ReadOnlyField
                label="Renter Licence Number"
                value={form.renter_licence_number}
              />
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Status */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Agreement Status
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{xs: 12, sm: 6}}>
            {editMode ? (
              <TextField
                label="Status"
                select
                fullWidth
                value={form.status}
                onChange={(e) => {
                  updateField('status', e.target.value);
                  clearFieldError('status');
                }}
                error={!!errors.status}
                helperText={errors.status}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="ended">Ended</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            ) : (
              <ReadOnlyField label="Status" value={form.status} />
            )}
          </Grid>

          {(form.status === 'ended' || form.status === 'cancelled') && (
            <Grid item size={{xs: 12}}>
              {editMode ? (
                <TextField
                  label="End Reason"
                  fullWidth
                  value={form.end_reason}
                  onChange={(e) => {
                    updateField('end_reason', e.target.value);
                    clearFieldError('end_reason');
                  }}
                  error={!!errors.end_reason}
                  helperText={errors.end_reason}
                />
              ) : (
                <ReadOnlyField
                  label="End Reason"
                  value={form.end_reason}
                />
              )}
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Meta */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Metadata
        </Typography>
        <Grid container spacing={2}>
          <Grid item size={{xs: 12, sm: 6}}>
            <ReadOnlyField
              label="Created At"
              value={formatDateTimeDisplay(agreement.created_at)}
            />
          </Grid>
          <Grid item size={{xs: 12, sm: 6}}>
            <ReadOnlyField
              label="Updated At"
              value={formatDateTimeDisplay(agreement.updated_at)}
            />
          </Grid>
        </Grid>

        {/* Actions row like EditVehicle: delete + close */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            mt: 3,
          }}
        >
          <Button
            color="error"
            variant="outlined"
            sx={{ textTransform: 'none' }}
            onClick={handleOpenDeleteAgreement}
          >
            Delete Hire Agreement
          </Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              sx={{ textTransform: 'none' }}
              onClick={() => navigate('/hire-agreements')}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Documents panel */}
      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Documents
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={handleUploadClick}
            >
              Upload
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </Box>

          {documentsLoading ? (
            <CircularProgress size={18} />
          ) : (
            <>
              {(!documents || documents.length === 0) && (
                <Typography variant="body2" color="text.secondary">
                  No documents uploaded for this hire agreement.
                </Typography>
              )}
              <List dense>
{documents.map((doc) => {
  const name = doc.filename || doc.originalName || 'Document';
  const typeLabel = doc.document_type
    ? doc.document_type.replace(/_/g, ' ')
    : 'Unknown';
  const sizeLabel = doc.size
    ? `${(doc.size / 1024).toFixed(1)} KB`
    : '—';
  const mimeLabel = doc.mime_type || '—';

  return (
    <ListItem
      key={doc._id}
      sx={{
        px: 0,
        py: 1,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        {/* Document name */}
        <Typography
          variant="subtitle2"
          noWrap
          sx={{ fontWeight: 500 }}
        >
          {name}
        </Typography>

        {/* Metadata row: type, size, mime */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {/* Document type */}
          <Chip
            label={typeLabel}
            size="small"
            variant="outlined"
            sx={{ textTransform: 'capitalize' }}
          />

          {/* Size */}
          <Typography
            variant="caption"
            color="text.secondary"
          >
            {sizeLabel}
          </Typography>

          {/* Separator dot */}
          <Typography
            variant="caption"
            color="text.secondary"
          >
            •
          </Typography>

          {/* MIME type */}
          <Typography
            variant="caption"
            color="text.secondary"
          >
            {mimeLabel}
          </Typography>
        </Box>
      </Box>

      {/* Actions */}
      <ListItemSecondaryAction>
        <IconButton
          edge="end"
          size="small"
          onClick={() => handleDownload(doc._id)}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
        <IconButton
          edge="end"
          size="small"
          sx={{ ml: 1 }}
          onClick={() => handleRequestDeleteDoc(doc)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );
})}

              </List>
            </>
          )}
        </Paper>
      </Box>

      {/* Floating FAB */}
      <Fab
        color={editMode ? 'secondary' : 'primary'}
        onClick={handleToggleEdit}
        disabled={saving}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          boxShadow: 4,
        }}
      >
        {editMode ? <SaveIcon /> : <EditIcon />}
      </Fab>

      {/* Delete hire agreement dialog (type "confirm") */}
      <Dialog
        open={deleteOpen}
        onClose={handleCloseDeleteAgreement}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete Hire Agreement</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2">
              This will permanently delete this hire agreement. To confirm, please
              type <strong>confirm</strong> in the box below. This action cannot
              be undone.
            </Typography>
            <TextField
              label='Type "confirm" to delete'
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteAgreement} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteAgreement}
            disabled={deleteAgreementDisabled || deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Confirm Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete document dialog (type "confirm") */}
      <Dialog
        open={fileDeleteOpen}
        onClose={handleCloseFileDelete}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Delete Document
          {fileToDelete && fileToDelete.filename
            ? ` – ${fileToDelete.filename}`
            : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2">
              This will permanently delete the selected document from this hire
              agreement. To confirm, please type{' '}
              <strong>confirm</strong> in the box below.
            </Typography>
            <TextField
              label='Type "confirm" to delete'
              value={fileDeleteInput}
              onChange={(e) => setFileDeleteInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileDelete} disabled={fileDeleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmFileDelete}
            disabled={fileDeleteDisabled || fileDeleteLoading}
          >
            {fileDeleteLoading ? (
              <CircularProgress size={20} />
            ) : (
              'Confirm Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    <Dialog
    open={uploadModalOpen}
    onClose={() => {
        setUploadModalOpen(false);
        setUploadErrors({});
    }}
    fullWidth
    maxWidth="sm"
    >
    <DialogTitle>Upload Document</DialogTitle>
    <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>

        <Typography variant="body2">
            Uploading: <strong>{uploadFile?.name}</strong>
        </Typography>

        <TextField
            required 
            label="File Name"
            type="text"
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={uploadFileName}
            onChange={(e) => setUploadFileName(e.target.value)}
            error={!!uploadErrors.filename}
            helperText={uploadErrors.filename}
        />

        {/* Document type */}
        <TextField
            required
            label="Document Type"
            select
            fullWidth
            value={uploadType}
            onChange={(e) => {
            setUploadType(e.target.value);
            setUploadErrors((p) => ({ ...p, document_type: undefined }));
            }}
            error={!!uploadErrors.document_type}
            helperText={uploadErrors.document_type}
        >
            <MenuItem value="contract">Contract</MenuItem>
            <MenuItem value="insurance">Insurance</MenuItem>
            <MenuItem value="other">Other</MenuItem>
        </TextField>

        {/* Expiration date (optional) */}
        <TextField
            label="Document Expiry Date (optional)"
            type="date"
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={uploadExpiry}
            onChange={(e) => setUploadExpiry(e.target.value)}
        />
        </Box>
    </DialogContent>

    <DialogActions>
        <Button
        onClick={() => {
            setUploadModalOpen(false);
            setUploadErrors({});
        }}
        >
        Cancel
        </Button>

        <Button
        variant="contained"
        onClick={async () => {
            const errs = {};
            if (!uploadType) errs.document_type = "Document type is required";

            if (Object.keys(errs).length > 0) {
            setUploadErrors(errs);
            return;
            }

            try {
            const formData = new FormData();
            formData.append("file", uploadFile);
            formData.append("filename", `${uploadFileName}.${uploadFile.name.split('.')[1]}`)
            formData.append("entity_type", "hire_agreement");
            formData.append("entity_id", id);
            formData.append("document_type", uploadType);
            if (uploadExpiry) formData.append("expiry_date", uploadExpiry);

            await uploadDocument(formData);

            setUploadModalOpen(false);
            setUploadErrors({});
            setUploadFile(null);
            setUploadType("");
            setUploadExpiry("");

            await loadDocuments();
            } catch (err) {
            console.error(err);
            setGenericError("Failed to upload document. Please try again.");
            }
        }}
        >
        Upload
        </Button>
    </DialogActions>
    </Dialog>
    </Box>
  );
}
